/**
 * Pure helpers for the drawing upload and migration flows. No canvas, no
 * Firebase, no pdf.js — everything here is unit-tested under jsdom.
 * The browser-side work (decode, render, embed) lives in blueprintSource.ts.
 */
import { BlueprintSource } from '../../types/protocols';

/** Long side of the derived raster of a drawing, in pixels (plan D6). */
export const DRAWING_RASTER_LONG_SIDE = 4000;

/** Long side of the page thumbnails shown in the PDF page picker. */
export const PDF_PAGE_THUMBNAIL_LONG_SIDE = 240;

/** Long side of the preview the crop step shows for a PDF page. */
export const DRAWING_CROP_PREVIEW_LONG_SIDE = 2000;

/** Smallest crop, as a fraction of each side: stops an accidental sliver. */
export const CROP_MIN_FRACTION = 0.05;

/** A cropped image whose long side is below this many pixels gets a warning (a PDF stays sharp). */
export const CROP_WARN_LONG_SIDE = 1000;

export const BLUEPRINT_SOURCE_CONTENT_TYPE = 'application/pdf' as const;

export type DrawingRasterMime = 'image/jpeg' | 'image/png';

/** Sizes `width × height` so its long side is at most `longSide`; never upscales. */
export function fitLongSide(
    width: number,
    height: number,
    longSide: number
): { width: number; height: number; scale: number } {
    const longest = Math.max(width, height);
    if (!(longest > 0)) {
        return { width: 0, height: 0, scale: 1 };
    }
    const scale = Math.min(1, longSide / longest);
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
        scale,
    };
}

/** Scale that makes the long side of a `width × height` box exactly `longSide` (may upscale: PDF points → pixels). */
export function scaleToLongSide(width: number, height: number, longSide: number): number {
    const longest = Math.max(width, height);
    return longest > 0 ? longSide / longest : 1;
}

/**
 * A crop rectangle as fractions (0..1) of the drawing as displayed (for a PDF:
 * after `/Rotate`), origin top-left.
 */
export interface CropFraction {
    x: number;
    y: number;
    width: number;
    height: number;
}

export const FULL_CROP: CropFraction = { x: 0, y: 0, width: 1, height: 1 };

const CROP_EPSILON = 0.0005;

/** Keeps the rectangle inside the drawing and at least CROP_MIN_FRACTION on each side. */
export function clampCropFraction(crop: CropFraction): CropFraction {
    const finite = (n: number, fallback: number) => (Number.isFinite(n) ? n : fallback);
    const width = Math.min(1, Math.max(CROP_MIN_FRACTION, finite(crop.width, 1)));
    const height = Math.min(1, Math.max(CROP_MIN_FRACTION, finite(crop.height, 1)));
    const x = Math.min(1 - width, Math.max(0, finite(crop.x, 0)));
    const y = Math.min(1 - height, Math.max(0, finite(crop.y, 0)));
    return { x, y, width, height };
}

/** True when the rectangle covers the whole drawing, i.e. nothing is cropped. */
export function isFullCrop(crop: CropFraction): boolean {
    return (
        crop.x <= CROP_EPSILON &&
        crop.y <= CROP_EPSILON &&
        crop.width >= 1 - CROP_EPSILON &&
        crop.height >= 1 - CROP_EPSILON
    );
}

export interface PdfBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Maps a crop of the displayed page to a page box in unrotated PDF user space
 * (y up). `box` is the page's current crop box, `rotation` its `/Rotate`
 * (clockwise, any multiple of 90). The result goes into both the media box
 * and the crop box; `/Rotate` stays as it is.
 */
export function cropBoxForPage(box: PdfBox, rotation: number, crop: CropFraction): PdfBox {
    // pdf-lib returns the rectangle as written; a box may be given corner-to-corner in any order.
    const x0 = Math.min(box.x, box.x + box.width);
    const y0 = Math.min(box.y, box.y + box.height);
    const w = Math.abs(box.width);
    const h = Math.abs(box.height);
    const { x: fx, y: fy, width: fw, height: fh } = clampCropFraction(crop);
    const quarterTurns = ((Math.round(rotation / 90) % 4) + 4) % 4;
    switch (quarterTurns) {
        case 1:
            return { x: x0 + fy * w, y: y0 + fx * h, width: fh * w, height: fw * h };
        case 2:
            return { x: x0 + (1 - fx - fw) * w, y: y0 + fy * h, width: fw * w, height: fh * h };
        case 3:
            return { x: x0 + (1 - fy - fh) * w, y: y0 + (1 - fx - fw) * h, width: fh * w, height: fw * h };
        default:
            return { x: x0 + fx * w, y: y0 + (1 - fy - fh) * h, width: fw * w, height: fh * h };
    }
}

/** The crop as a whole-pixel rectangle inside a `width × height` image. */
export function cropPixelRect(
    width: number,
    height: number,
    crop: CropFraction
): { x: number; y: number; width: number; height: number } {
    const c = clampCropFraction(crop);
    const x = Math.min(width - 1, Math.max(0, Math.round(c.x * width)));
    const y = Math.min(height - 1, Math.max(0, Math.round(c.y * height)));
    return {
        x,
        y,
        width: Math.max(1, Math.min(width - x, Math.round(c.width * width))),
        height: Math.max(1, Math.min(height - y, Math.round(c.height * height))),
    };
}

/** JPEG input stays JPEG; everything else is re-encoded as PNG (plan D18). */
export function rasterMimeForImage(inputType: string): DrawingRasterMime {
    return inputType.toLowerCase() === 'image/jpeg' ? 'image/jpeg' : 'image/png';
}

/** HEIC/HEIF is rejected at the modal (plan D18): browsers other than Safari cannot decode it. */
export function isHeicFile(file: { type: string; name: string }): boolean {
    const type = file.type.toLowerCase();
    if (type === 'image/heic' || type === 'image/heif' || type === 'image/heic-sequence' || type === 'image/heif-sequence') {
        return true;
    }
    return /\.(heic|heif)$/i.test(file.name);
}

export const HEIC_REJECTED_MESSAGE =
    'HEIC/HEIF-bilder kan inte användas som ritning. Konvertera bilden till JPG eller PNG först.';

export interface BuildBlueprintSourceInput {
    storagePath: string;
    fileSize: number;
    origin: BlueprintSource['origin'];
    author: string;
    date: string;
    sourcePage?: number;
    sourceFileName?: string;
}

/** Builds the `source` node without undefined values (Firebase rejects them). */
export function buildBlueprintSource(input: BuildBlueprintSourceInput): BlueprintSource {
    const source: BlueprintSource = {
        storagePath: input.storagePath,
        fileSize: input.fileSize,
        contentType: BLUEPRINT_SOURCE_CONTENT_TYPE,
        origin: input.origin,
        author: input.author,
        date: input.date,
    };
    if (input.sourcePage !== undefined) {
        source.sourcePage = input.sourcePage;
    }
    if (input.sourceFileName) {
        source.sourceFileName = input.sourceFileName;
    }
    return source;
}

/** True when a drawing node carries a usable `source` (a non-empty `storagePath`). */
export function hasBlueprintSource(node: unknown): boolean {
    if (!node || typeof node !== 'object') return false;
    const source = (node as { source?: unknown }).source;
    if (!source || typeof source !== 'object') return false;
    const storagePath = (source as { storagePath?: unknown }).storagePath;
    return typeof storagePath === 'string' && storagePath.length > 0;
}
